/**
 * Sticky site header.
 *
 * Uses stickybits to pin `.site-header` as the page scrolls, while keeping it
 * clear of the WP admin bar. The admin bar changes behaviour across breakpoints
 * (32px fixed, 46px fixed at/below 782px, and absolutely positioned so it
 * scrolls away at/below 600px), so we compute the stick offset from those
 * breakpoints via matchMedia and fully re-initialise stickybits whenever we
 * cross one, rather than calling update() which leaves a stale inline `top`.
 * The visible header height is published as the `--qala-header-height` CSS
 * custom property (measured from the inner container for the transparent
 * header, whose own height is 0), recomputed on resize and after init. For the
 * transparent header we also toggle a `js-show-transparent-header` class once
 * the user scrolls a filterable distance past the admin bar, gated behind
 * requestAnimationFrame, and expose that threshold as `--qala-header-offset` so
 * the scroll-driven reveal animation lines up with the class flip. The whole
 * behaviour can be disabled through the `qala_theme.filter.pre_disable_sticky_menu`
 * filter.
 *
 * The `qala_theme.filter.sticky_usp_bar` and `qala_theme.filter.sticky_info_bar`
 * filters (passed the bar element) pin that bar with the header, in render order.
 * The combined pinned height is published as `--qala-sticky-header-height`.
 */

import stickybits from 'stickybits';

/**
 * Finds the header bars filtered to stick with the header.
 *
 * Only siblings of the header count, so the checkout USP bar and info bar modules are
 * left alone. The info bar may sit inside `<esi:remove>` when ESI is not processed.
 *
 * @param {HTMLElement} header The site header.
 *
 * @return {{above: HTMLElement[], below: HTMLElement[]}} The bars, in document order.
 */
const getStickyBars = header => {
	const bars = { above: [], below: [] };
	const container = header.parentElement;

	if ( header.classList.contains( 'site-header--transparent' ) ) {
		return bars;
	}

	Array.from( container.querySelectorAll( '.usp-bar, .info-bar' ) ).filter(
		el => el.parentElement === container || el.parentElement.parentElement === container
	).forEach( bar => {
		const filter = bar.classList.contains( 'usp-bar' ) ? 'qala_theme.filter.sticky_usp_bar' : 'qala_theme.filter.sticky_info_bar';
		if ( ! wp.hooks.applyFilters( filter, false, bar ) ) {
			return;
		}

		// eslint-disable-next-line no-bitwise
		const isAbove = header.compareDocumentPosition( bar ) & window.Node.DOCUMENT_POSITION_PRECEDING;
		bars[ isAbove ? 'above' : 'below' ].push( bar );
	} );

	return bars;
};

jQuery( $ => { // eslint-disable-line no-unused-vars
	const disableStickyMenu = wp.hooks.applyFilters( 'qala_theme.filter.pre_disable_sticky_menu', false );

	const setHeaderHeight = () => {
		const header = document.querySelector( '.site-header' );
		if ( header ) {
			const measureEl = header.classList.contains( 'site-header--transparent' )
				? header.querySelector( ':scope > .container' ) || header
				: header;
			const headerHeight = measureEl.offsetHeight;
			document.documentElement.style.setProperty( '--qala-header-height', `${ headerHeight }px` );
			document.body.style.setProperty( '--qala-header-height', `${ headerHeight }px` );
		}
	};

	setHeaderHeight();

	let resizeTimer;
	window.addEventListener( 'resize', () => {
		clearTimeout( resizeTimer );
		resizeTimer = setTimeout( () => {
			setHeaderHeight();
		}, 250 );
	} );

	if ( ! disableStickyMenu ) {
		const header = document.querySelector( '.site-header' );
		const adminBar = document.getElementById( 'wpadminbar' );

		const adminBarQuery = window.matchMedia( '(max-width: 782px)' );
		const adminBarAbsoluteQuery = window.matchMedia( '(max-width: 600px)' );

		const getAdminBarHeight = () => {
			if ( ! adminBar ) {
				return 0;
			}
			if ( adminBarAbsoluteQuery.matches ) {
				return 0;
			}
			return adminBarQuery.matches ? 46 : 32;
		};

		const stickyBars = header ? getStickyBars( header ) : { above: [], below: [] };
		let barsAboveHeight = 0;

		let instance;
		const initStickybits = () => {
			if ( instance ) {
				instance.cleanup();
				instance = null;
			}

			instance = stickybits( '.site-header', {
				useStickyClasses: true,
				useGetBoundingClientRect: true,
				stickyBitStickyOffset: getAdminBarHeight() + barsAboveHeight,
			} );
		};

		/**
		 * Stacks the bars above the header from the admin bar down, then the header, then the bars below it.
		 */
		const layoutStickyBars = () => {
			let top = getAdminBarHeight();
			stickyBars.above.forEach( bar => {
				bar.style.setProperty( '--qala-sticky-bar-top', `${ top }px` );
				top += bar.offsetHeight;
			} );

			const newBarsAboveHeight = top - getAdminBarHeight();
			if ( newBarsAboveHeight !== barsAboveHeight ) {
				barsAboveHeight = newBarsAboveHeight;
				// Stickybits bakes the offset into an inline `top`, so a new offset needs a fresh instance.
				initStickybits();
			}

			top += header.offsetHeight;
			stickyBars.below.forEach( bar => {
				bar.style.setProperty( '--qala-sticky-bar-top', `${ top }px` );
				top += bar.offsetHeight;
			} );

			const stickyHeight = `${ top - getAdminBarHeight() }px`;
			document.documentElement.style.setProperty( '--qala-sticky-header-height', stickyHeight );
			document.body.style.setProperty( '--qala-sticky-header-height', stickyHeight );
		};

		initStickybits();

		const hasStickyBars = stickyBars.above.length > 0 || stickyBars.below.length > 0;
		if ( hasStickyBars ) {
			[ ...stickyBars.above, ...stickyBars.below ].forEach( bar => bar.classList.add( 'js-is-sticky-header-bar' ) );
			layoutStickyBars();

			// Deferred a frame: re-initialising stickybits can resize the header, which would loop the observer.
			const resizeObserver = new window.ResizeObserver( () => window.requestAnimationFrame( layoutStickyBars ) );
			[ header, ...stickyBars.above, ...stickyBars.below ].forEach( el => resizeObserver.observe( el ) );
		}

		const showTransparentHeaderClass = 'js-show-transparent-header';
		const transparentHeaderOffset = wp.hooks.applyFilters(
			'qala_theme.filter.transparent_header_scroll_offset',
			100
		);

		let transparentThreshold = getAdminBarHeight() + transparentHeaderOffset;
		let transparentActive = false;
		let transparentTicking = false;

		const setTransparentOffset = () => {
			document.documentElement.style.setProperty( '--qala-header-offset', `${ transparentThreshold }px` );
			document.body.style.setProperty( '--qala-header-offset', `${ transparentThreshold }px` );
		};

		setTransparentOffset();

		const updateTransparentHeader = () => {
			transparentTicking = false;
			if ( ! header ) {
				return;
			}
			const shouldShow = window.scrollY >= transparentThreshold;
			if ( shouldShow !== transparentActive ) {
				transparentActive = shouldShow;
				header.classList.toggle( showTransparentHeaderClass, shouldShow );
			}
		};

		window.addEventListener( 'scroll', () => {
			if ( ! transparentTicking ) {
				transparentTicking = true;
				window.requestAnimationFrame( updateTransparentHeader );
			}
		}, { passive: true } );

		updateTransparentHeader();

		const handleAdminBarBreakpoint = () => {
			initStickybits();
			if ( hasStickyBars ) {
				layoutStickyBars();
			}
			transparentThreshold = getAdminBarHeight() + transparentHeaderOffset;
			setTransparentOffset();
			updateTransparentHeader();
			setHeaderHeight();
		};

		if ( adminBar ) {
			adminBarQuery.addEventListener( 'change', handleAdminBarBreakpoint );
			adminBarAbsoluteQuery.addEventListener( 'change', handleAdminBarBreakpoint );
		}

		setTimeout( setHeaderHeight, 100 );
	}
} );
